import { createContext, useContext, useRef, useCallback, useMemo, useEffect, ReactNode } from 'react';
import { useUser } from './UserContext';

interface CacheEntry<T = unknown> {
    data: T;
    timestamp: number;
}

interface QueryBuilderCache {
    get<T>(key: string): CacheEntry<T> | undefined;
    set<T>(key: string, data: T): void;
    invalidate(keyPrefix?: string): void;
}

const QueryBuilderCacheContext = createContext<QueryBuilderCache | null>(null);

export const QueryBuilderCacheProvider = ({ children }: { children: ReactNode }) => {
    const store = useRef<Map<string, CacheEntry>>(new Map());
    const { selectedWorkspace } = useUser();

    const get = useCallback(<T,>(key: string) => store.current.get(key) as CacheEntry<T> | undefined, []);

    const set = useCallback(<T,>(key: string, data: T) => {
        store.current.set(key, { data, timestamp: Date.now() });
    }, []);

    const invalidate = useCallback((keyPrefix?: string) => {
        if (!keyPrefix) {
            store.current.clear();
            return;
        }
        store.current.forEach((_, key) => {
            if (key.startsWith(keyPrefix)) store.current.delete(key);
        });
    }, []);

    useEffect(() => {
        store.current.clear();
    }, [selectedWorkspace?.id]);

    const value = useMemo(() => ({ get, set, invalidate }), [get, set, invalidate]);

    return <QueryBuilderCacheContext.Provider value={value}>{children}</QueryBuilderCacheContext.Provider>;
};

export const useQueryBuilderCache = () => useContext(QueryBuilderCacheContext);
